/**
 * P227 -- per-service telemetry history chart (the row drill-in). A
 * hand-rolled multi-line SVG (no chart lib, per the P2.9 precedent):
 * availability / latency / error, each normalised to its own band over a
 * shared x = time. Reuses the sparkline normalisation.
 */
import { useTranslation } from "react-i18next";

import type { ServiceHistoryPoint } from "shared/api/types";

import { normaliseSeries } from "./sparkline";

const W = 520;
const H = 120;
const PAD = 6;

function linePath(yfracs: number[]): string {
  if (yfracs.length < 2) return "";
  const step = (W - 2 * PAD) / (yfracs.length - 1);
  return yfracs
    .map((f, i) => {
      const x = PAD + i * step;
      const y = H - PAD - f * (H - 2 * PAD);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

export function ServiceHistoryChart({
  points,
}: {
  points: ServiceHistoryPoint[];
}): JSX.Element {
  const { t } = useTranslation();
  if (points.length < 2) {
    return (
      <p data-testid="telemetry-history-empty" className="text-xs text-slate-400">
        {t("modules.twin.telemetry.history.empty")}
      </p>
    );
  }
  const avail = normaliseSeries(
    points.map((p) => p.availability_pct ?? 0),
    "pct",
  );
  const latency = normaliseSeries(
    points.map((p) => p.latency_ms ?? 0),
    "max",
  );
  const error = normaliseSeries(
    points.map((p) => p.error_rate_pct ?? 0),
    "pct",
  );
  const lines: { key: string; yfracs: number[]; cls: string }[] = [
    { key: "availability", yfracs: avail, cls: "stroke-rag-green" },
    { key: "latency", yfracs: latency, cls: "stroke-brand-brassDark" },
    { key: "error", yfracs: error, cls: "stroke-rag-red" },
  ];
  return (
    <div data-testid="telemetry-history-chart" className="flex flex-col gap-2">
      <svg
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        className="max-w-full"
        role="img"
        aria-label={t("modules.twin.telemetry.history.title")}
      >
        <rect x="0" y="0" width={W} height={H} className="fill-slate-50" rx="4" />
        {lines.map((l) => (
          <path
            key={l.key}
            data-testid={`telemetry-history-line-${l.key}`}
            d={linePath(l.yfracs)}
            fill="none"
            strokeWidth="1.5"
            strokeLinejoin="round"
            className={l.cls}
          />
        ))}
      </svg>
      <div className="flex flex-wrap gap-3 text-[10px] text-slate-500">
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full bg-rag-green" />
          {t("modules.twin.telemetry.history.availability")}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full bg-brand-brassDark" />
          {t("modules.twin.telemetry.history.latency")}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full bg-rag-red" />
          {t("modules.twin.telemetry.history.errorRate")}
        </span>
        <span className="ms-auto tabular-nums">
          {t("modules.twin.telemetry.history.points", { n: points.length })}
        </span>
      </div>
    </div>
  );
}
